/**
 * Decision-frame taxonomy — gibbs-question-chooser.
 *
 * v1 catalog of decision frames a chemist can pick from at the
 * start of (or during) a session. Each frame answers
 *   "what are you trying to decide?"
 * and biases downstream behavior — LLM prompt prefix, ResultsPane
 * emphasis, decision-record `frame` field.
 *
 * @module gibbs/question-chooser/frames
 */

/**
 * @typedef {Object} Frame
 * @property {string} id - Stable id, used as the persistence key
 *   and the decision-record `frame` field.
 * @property {string} label - Short user-facing chip label
 *   (≤ 40 chars).
 * @property {string} description - One-sentence prose shown on the
 *   chooser card.
 * @property {string} promptPrefix - Sentence injected at the top of
 *   the LLM system prompt when this frame is active.
 * @property {string[]} suggestedActions - Registry action IDs that
 *   compose this frame's typical workflow. Used by the chooser to
 *   render quick-actions and by future ResultsPane emphasis logic.
 */

/** @type {Frame[]} */
export const FRAMES = [
    {
        id: 'pick-best-hit',
        label: 'Pick the best hit from a batch',
        description: 'You have a docked-and-scored batch and need to '
            + 'choose which compound(s) to advance.',
        promptPrefix: 'The user is trying to decide which compound to '
            + 'advance from a docked-and-scored batch. Bias rankings, '
            + 'rationales, and suggested actions toward score, flag '
            + 'count, and interaction quality.',
        suggestedActions: ['dock-compound', 'predict-gnina-scores',
            'energy-minimize', 'scan-structural-alerts'],
    },
    {
        id: 'optimize-analog',
        label: 'Decide whether to optimize this analog',
        description: 'You have a single compound and need to decide '
            + 'whether to invest more SAR cycles in it.',
        promptPrefix: 'The user is triaging a single compound for '
            + 'continued optimization. Surface property liabilities, '
            + 'interaction gaps, and the most-impactful next move.',
        suggestedActions: ['scan-structural-alerts', 'energy-minimize',
            'predict-gnina-scores'],
    },
    {
        id: 'find-bioisostere',
        label: 'Find a bioisostere for this fragment',
        description: 'You have a fragment you want to swap for one with '
            + 'better properties while keeping the binding mode.',
        promptPrefix: 'The user is searching for a bioisostere replacement. '
            + 'Bias toward the medchem-rules catalog and the '
            + 'replace-with-bioisostere action; cite Meanwell-style '
            + 'rules where applicable.',
        suggestedActions: ['replace-with-bioisostere',
            'swap-heteroaromatic-ring'],
    },
    {
        id: 'mitigate-toxicophore',
        label: 'Mitigate a toxicophore',
        description: 'You see a structural-alert pattern and want to '
            + 'block the bioactivation pathway.',
        promptPrefix: 'The user is mitigating a toxicophore alert. '
            + 'Bias toward scan-structural-alerts followed by '
            + 'mitigate-toxicophore, citing the catalog\'s mitigation '
            + 'recipes.',
        suggestedActions: ['scan-structural-alerts', 'mitigate-toxicophore'],
    },
    {
        id: 'compare-analogs',
        label: 'Compare two analogs',
        description: 'You want a side-by-side view of two compounds to '
            + 'see exactly what differs.',
        promptPrefix: 'The user is performing a pairwise comparison of '
            + 'two compounds. Bias toward opening the CompoundComparePane '
            + 'and surfacing the property + energy diff.',
        suggestedActions: [],
    },
    {
        id: 'identify-active-site',
        label: 'Identify the active site on this protein',
        description: 'You loaded a new protein and need to figure out '
            + 'where the binding pocket is. (Theme I — depends on '
            + 'Dan\'s PDF upload.)',
        promptPrefix: 'The user is identifying the active site of a '
            + 'loaded protein. Bias toward literature-grounded '
            + 'extraction; surface 3D-viewer highlights when residues '
            + 'are named.',
        suggestedActions: [],
    },
    {
        id: 'tune-interaction',
        label: 'Tune a specific interaction',
        description: 'You see a pi-pi / H-bond / halogen / salt-bridge '
            + 'contact and want to strengthen it.',
        promptPrefix: 'The user is tuning a specific protein-ligand '
            + 'interaction (pi-pi, H-bond, halogen, salt-bridge, '
            + 'cation-π). Bias toward the interaction-tuning rule set; '
            + 'cite Meanwell complementarity principles.',
        suggestedActions: [],
    },
    {
        id: 'generate-new-compounds',
        label: 'Generate new compounds',
        description: 'You want de-novo / fragment-grow output from the '
            + 'current target or template.',
        promptPrefix: 'The user is generating new compounds (de novo / '
            + 'fragment-grow / reinvent). Bias toward the hit-discovery '
            + 'or virtual-screen workflows.',
        suggestedActions: ['grow-from-all-atoms', 'job-reinvent',
            'job-library-screen'],
    },
    {
        id: 'none-of-the-above',
        label: 'None of the above — open-ended chat',
        description: 'Skip framing and use the regular verb-typing '
            + 'interface.',
        promptPrefix: '',
        suggestedActions: [],
    },
];

/**
 * Lookup a frame by id.
 *
 * @param {string|null|undefined} id
 * @returns {Frame|null}
 */
export function getFrameById(id) {
    if (!id) return null;
    return FRAMES.find((f) => f.id === id) || null;
}

/**
 * Whether a given id refers to the "escape hatch" (no-frame) case.
 *
 * @param {string|null|undefined} id
 * @returns {boolean}
 */
export function isEscapeFrame(id) {
    return id === 'none-of-the-above';
}
